import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface StreamingListingRow {
  id: string
  title: string
  content_type: string
  provider: string
  service: string
  genre_primary: string | null
  genre_secondary: string | null
  /** First segment of genre when split by `>` or `|` (used for filter) */
  primary_genre: string | null
  release_year: number | null
  age_rating: string | null
  audio_language: string | null
  audio_description_available: boolean | null
  subtitles_available: boolean | null
  sign_language_available: boolean | null
  access_model: string
  /** When set, lists all entitlement types (e.g. Buy, Rent); UI shows all. */
  access_models: string[] | null
  subscription_tier: string | null
  video_quality: string | null
  deeplink_url: string | null
  territory: string | null
  /** TMDB poster image URL; null → TvListingImage shows TvFallbackLogo */
  poster_url: string | null
  backdrop_url: string | null
  logo_url: string | null
  /** TMDB overview/synopsis; set by enrich_streaming_tmdb. Shown in details modal. */
  description: string | null
  /** After this instant (UTC) the row is hidden on Streaming until re-ingested. Set by ingest. */
  catalogue_expires_at?: string
}

export interface StreamingListingsFilters {
  /** Case-insensitive partial match on title */
  title_search?: string
  /** Filter by first character of title: "#" (special), "0-9" (digits), or "A"-"Z" (letter) */
  title_starts_with?: string
  service?: string
  provider?: string
  content_type?: string
  /** Filter by primary_genre (first segment of genre by `>` or `|`) */
  primary_genre?: string
  access_model?: string
  /** When true, only include included_with_subscription or free_ad_supported */
  free_only?: boolean
  audio_language?: string
  audio_description_available?: boolean
  subtitles_available?: boolean
  sign_language_available?: boolean
}

const PAGE_SIZE = 10

export interface UseStreamingListingsOptions {
  /** When set, refetch list this often (ms) while mounted. Use on Streaming page so images update after enrichment without relying on Realtime. */
  refetchIntervalMs?: number
}

/**
 * @param enabledLanguages When set (from Language Control), only listings with audio_language in this list are returned. When empty array, no results. When null/undefined, filter not applied (e.g. while loading).
 */
export function useStreamingListings(
  filters: StreamingListingsFilters,
  page: number = 1,
  enabledLanguages: string[] | null | undefined = null,
  options?: UseStreamingListingsOptions
) {
  return useQuery({
    queryKey: ['streaming-listings', filters, page, enabledLanguages],
    refetchInterval: options?.refetchIntervalMs,
    queryFn: async () => {
      if (Array.isArray(enabledLanguages) && enabledLanguages.length === 0) {
        return { rows: [], total: 0, page, pageSize: PAGE_SIZE }
      }
      const nowIso = new Date().toISOString()
      let q = supabase
        .from('streaming_listings')
        .select('*', { count: 'exact' })
        // Legacy rows may have NULL expiry; treat them as visible until backfilled.
        .or(`catalogue_expires_at.is.null,catalogue_expires_at.gt.${nowIso}`)
        .order('title')
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1)

      if (Array.isArray(enabledLanguages) && enabledLanguages.length > 0) {
        // Include NULL: legacy rows and n8n-ingested CSVs may lack language until backfilled.
        const langList = enabledLanguages.map((c) => c.replace(/,/g, '')).join(',')
        q = q.or(`audio_language.in.(${langList}),audio_language.is.null`)
      }

      const titleSearch = filters.title_search?.trim()
      if (titleSearch) {
        const escaped = titleSearch.replace(/[%_]/g, (ch) => (ch === '%' ? '\\%' : '\\_'))
        q = q.ilike('title', `%${escaped}%`)
      }

      const startWith = filters.title_starts_with?.trim()
      if (startWith) {
        if (startWith === '#') {
          const excludePatterns = ['0%', '1%', '2%', '3%', '4%', '5%', '6%', '7%', '8%', '9%']
          for (let i = 0; i < 26; i++) excludePatterns.push(`${String.fromCharCode(97 + i)}%`)
          for (const p of excludePatterns) {
            q = q.not('title', 'ilike', p)
          }
        } else if (startWith === '0-9') {
          q = q.or('title.ilike.0%,title.ilike.1%,title.ilike.2%,title.ilike.3%,title.ilike.4%,title.ilike.5%,title.ilike.6%,title.ilike.7%,title.ilike.8%,title.ilike.9%')
        } else if (/^[A-Za-z]$/.test(startWith)) {
          q = q.ilike('title', `${startWith.toLowerCase()}%`)
        }
      }

      if (filters.service) q = q.eq('service', filters.service)
      if (filters.provider) q = q.eq('provider', filters.provider)
      if (filters.content_type) q = q.eq('content_type', filters.content_type)
      if (filters.primary_genre) q = q.eq('primary_genre', filters.primary_genre)
      if (filters.audio_language) q = q.eq('audio_language', filters.audio_language)
      if (filters.access_model) {
        q = q.or(`access_model.eq.${filters.access_model},access_models.ov.{${filters.access_model}}`)
      }
      if (filters.free_only) {
        q = q.in('access_model', ['included_with_subscription', 'free_ad_supported'])
      }
      if (filters.audio_description_available === true) {
        q = q.eq('audio_description_available', true)
      }
      if (filters.subtitles_available === true) {
        q = q.eq('subtitles_available', true)
      }
      if (filters.sign_language_available === true) {
        q = q.eq('sign_language_available', true)
      }

      const { data, error, count } = await q
      if (error) throw error
      return {
        rows: (data ?? []) as StreamingListingRow[],
        total: count ?? 0,
        page,
        pageSize: PAGE_SIZE,
      }
    },
    staleTime: 60_000,
  })
}

export const STREAMING_FILTER_OPTIONS_QUERY_KEY = ['streaming-filter-options']

export interface StreamingFilterOptions {
  services: string[]
  providers: string[]
  content_types: string[]
  genres: string[]
  access_models: string[]
  languages: string[]
}

export function useStreamingFilterOptions() {
  return useQuery({
    queryKey: STREAMING_FILTER_OPTIONS_QUERY_KEY,
    queryFn: async (): Promise<StreamingFilterOptions> => {
      const { data, error } = await supabase.rpc('get_streaming_filter_options')
      if (error) throw error
      const raw = (Array.isArray(data) ? data[0] : data) as {
        services?: string[]
        providers?: string[]
        content_types?: string[]
        genres?: string[]
        access_models?: string[]
        languages?: string[]
      } | null
      if (!raw) {
        return { services: [], providers: [], content_types: [], genres: [], access_models: [], languages: [] }
      }
      return {
        services: Array.isArray(raw.services) ? raw.services : [],
        providers: Array.isArray(raw.providers) ? raw.providers : [],
        content_types: Array.isArray(raw.content_types) ? raw.content_types : [],
        genres: Array.isArray(raw.genres) ? raw.genres : [],
        access_models: Array.isArray(raw.access_models) ? raw.access_models : [],
        languages: Array.isArray(raw.languages) ? raw.languages : [],
      }
    },
    staleTime: 120_000,
  })
}
